import { Button } from "@aurumiq/ui/components/button";
import { Input } from "@aurumiq/ui/components/input";
import { Label } from "@aurumiq/ui/components/label";
import { Switch } from "@aurumiq/ui/components/switch";
import { Textarea } from "@aurumiq/ui/components/textarea";
import { Trash2 } from "lucide-react";
import { useId, useState } from "react";
import { SchemaForm } from "../schema-form/schema-form";
import {
  BLOCK_BASE_KEYS,
  BLOCK_FIELDS,
  BLOCK_LABELS,
  type EditorBlock,
  isBlockType,
  validateBlock,
} from "./blocks";

export function BlockForm({
  block,
  onChange,
  onDelete,
}: {
  block: EditorBlock;
  onChange: (raw: Record<string, unknown>) => void;
  onDelete: () => void;
}) {
  const id = useId();
  const v = validateBlock(block.raw);
  const type = block.raw.type;

  if (!isBlockType(type))
    return <UnknownBlock block={block} onChange={onChange} onDelete={onDelete} />;

  const topIssue = v.issues.find((i) => i.path.length === 0)?.message;
  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-semibold">{BLOCK_LABELS[type]}</h2>
        <div className="flex items-center gap-3">
          <Label htmlFor={`${id}-hidden`} className="text-muted-foreground text-xs">
            Hidden
          </Label>
          <Switch
            id={`${id}-hidden`}
            checked={block.raw.hidden === true}
            onCheckedChange={(c) => onChange({ ...block.raw, hidden: c })}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-destructive"
            onClick={onDelete}
          >
            <Trash2 /> Delete
          </Button>
        </div>
      </div>
      {!v.ok ? (
        <div
          role="alert"
          className="rounded-lg border border-danger/40 bg-danger-bg px-3 py-2 text-danger text-xs"
        >
          {v.issues.length} problem{v.issues.length > 1 ? "s" : ""} — fix the highlighted fields.
          {topIssue ? ` ${topIssue}` : ""}
        </div>
      ) : null}
      <div>
        <Label htmlFor={`${id}-anchor`}>Anchor (optional)</Label>
        <Input
          id={`${id}-anchor`}
          value={typeof block.raw.anchor === "string" ? block.raw.anchor : ""}
          placeholder="section-id"
          onChange={(e) => {
            const next = { ...block.raw };
            if (e.target.value) next.anchor = e.target.value;
            else delete next.anchor;
            onChange(next);
          }}
        />
      </div>
      <SchemaForm
        fields={BLOCK_FIELDS[type]}
        value={block.raw}
        onChange={(next) => onChange({ ...next, id: block.raw.id, type })}
        issues={v.issues}
        omit={BLOCK_BASE_KEYS}
      />
    </div>
  );
}

/** Unknown / malformed block: shown as raw JSON so it can be fixed or removed without crashing. */
function UnknownBlock({
  block,
  onChange,
  onDelete,
}: {
  block: EditorBlock;
  onChange: (raw: Record<string, unknown>) => void;
  onDelete: () => void;
}) {
  const [text, setText] = useState(() => JSON.stringify(block.raw, null, 2));
  const [err, setErr] = useState<string | null>(null);
  return (
    <div className="grid gap-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-semibold text-danger">
          Unknown block type “{String(block.raw.type ?? "?")}”
        </h2>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-destructive"
          onClick={onDelete}
        >
          <Trash2 /> Delete
        </Button>
      </div>
      <p className="text-muted-foreground text-sm">
        This block is not in the schema, so the landing site will not render it. Edit the JSON (set{" "}
        <code className="font-mono">type</code> to a known block) or delete it.
      </p>
      <Textarea
        aria-label="Block JSON"
        rows={12}
        className="font-mono text-xs"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          try {
            const parsed = JSON.parse(text);
            if (typeof parsed !== "object" || parsed === null) throw new Error("Must be an object");
            setErr(null);
            onChange(parsed as Record<string, unknown>);
          } catch (e) {
            setErr(e instanceof Error ? e.message : String(e));
          }
        }}
      />
      {err ? <p className="text-destructive text-xs">{err}</p> : null}
    </div>
  );
}
